import { ConnectorTemplate, ConnectorFieldMapping } from "../../types"

/**
 * Predefined connector templates for common CRM integrations
 * These provide smart defaults that users can customize
 */

// Lead field mappings
export const ZAPIER_LEAD_MAPPING: ConnectorFieldMapping = {
  first_name: { key: "first_name" },
  last_name: { key: "last_name" },
  email: { key: "email" },
  phone: { key: "phone" },
  loan_purpose: { key: "details.loan_purpose" },
  loan_type: { key: "details.loan_type" },
  loan_amount: { key: "details.loan_amount" },
  credit_score: { key: "details.credit_score" },
  city: { key: "details.location.city" },
  state: { key: "details.location.state" },
  zipcode: { key: "details.location.zipcode" },
  message: { key: "details.message" }
}

export const GENERIC_LEAD_MAPPING: ConnectorFieldMapping = {
  firstname: { key: "first_name" },
  lastname: { key: "last_name" },
  email: { key: "email" },
  phone: { key: "phone" },
  creditscore: { key: "details.credit_score" },
  downpayment: { key: "details.down_payment" },
  listprice: { key: "details.list_price" },
  loanamount: { key: "details.loan_amount" },
  loanpurpose: { key: "details.loan_purpose" },
  loantype: { key: "details.loan_type" },
  loanterm: { key: "details.loan_term" },
  propertytype: { key: "details.property_type" },
  residencytype: { key: "details.residency_type" },
  realtor: { key: "details.realtor" },
  city: { key: "details.location.city" },
  state: { key: "details.location.state" },
  zipcode: { key: "details.location.zipcode" },
  streetaddress: { key: "details.location.street_address" },
  county: { key: "details.location.county" },
  message: { key: "details.message" }
}

// Subscription field mappings
export const ZAPIER_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = {
  phone: { key: "phone" },
  email: { key: "email" },
  loid: { key: "loid" },
  first_name: { key: "first_name" },
  last_name: { key: "last_name" },
  loan_purpose: { key: "quote_info.loan_purpose" },
  credit_score: { key: "quote_info.credit_score" },
  property_type: { key: "quote_info.property_type" },
  residency_type: { key: "quote_info.residency_type" },
  list_price: { key: "quote_info.list_price" },
  loan_amount: { key: "quote_info.loan_amount" },
  loan_term: { key: "quote_info.loan_term" },
  loan_type: { key: "quote_info.loan_type" },
  state: { key: "quote_info.location.state" },
  city: { key: "quote_info.location.city" },
  zipcode: { key: "quote_info.location.zipcode" },
  street_address: { key: "quote_info.location.street_address" },
  county: { key: "quote_info.location.county" }
}

// Pending subscription field mappings
export const ZAPIER_PENDING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = {
  phone: { key: "phone" },
  email: { key: "email" },
  loid: { key: "loid" },
  first_name: { key: "first_name" },
  last_name: { key: "last_name" },
  trigger_rate: { key: "trigger_rate" },
  loan_purpose: { key: "quote_info.loan_purpose" },
  credit_score: { key: "quote_info.credit_score" },
  property_type: { key: "quote_info.property_type" },
  residency_type: { key: "quote_info.residency_type" },
  list_price: { key: "quote_info.list_price" },
  loan_amount: { key: "quote_info.loan_amount" },
  loan_term: { key: "quote_info.loan_term" },
  loan_type: { key: "quote_info.loan_type" },
  state: { key: "quote_info.location.state" },
  city: { key: "quote_info.location.city" },
  zipcode: { key: "quote_info.location.zipcode" },
  street_address: { key: "quote_info.location.street_address" },
  county: { key: "quote_info.location.county" }
}

// Own Up incoming lead mapping (for receiving leads from Own Up)
export const OWNUP_INCOMING_LEAD_MAPPING: ConnectorFieldMapping = {
  leadID: {
    key: "details.source_lead_id"
  },
  leadCost: {
    key: "details.lead_cost"
  },
  subsource: {
    key: "details.subsource"
  },
  firstName: {
    key: "first_name"
  },
  lastName: {
    key: "last_name"
  },
  email: {
    key: "email"
  },
  phoneNumber: {
    key: "phone"
  },
  creditRating: {
    key: "details.credit_score",
    values: {
      Excellent: 800,
      Good: 700,
      Fair: 650,
      Poor: 600,
      "Very Poor": 550,
      default: 650
    }
  },
  vaEligible: {
    key: "details.va_eligible"
  },
  fthb: {
    key: "details.first_time_buyer"
  },
  bankruptcy: {
    key: "details.foreclosure_bankruptcy"
  },
  hasAgent: {
    key: "details.has_agent"
  },
  hasCoborrower: {
    key: "details.has_coborrower"
  },
  incomeType: {
    key: "details.income_type",
    values: {
      Salary: "salary",
      Hourly: "hourly",
      Bonus: "bonus",
      Commission: "commission",
      Pension: "pension",
      SelfEmployed: "self_employed",
      NoIncome: "no_income",
      Other: "other",
      default: "salary"
    }
  },
  grossIncome: {
    key: "details.household_income"
  },
  propertyStreet: {
    key: "details.location.street_address"
  },
  propertyCity: {
    key: "details.location.city"
  },
  propertyState: {
    key: "details.location.state"
  },
  propertyCounty: {
    key: "details.location.county"
  },
  propertyZip: {
    key: "details.location.zipcode"
  },
  propertyValue: {
    key: "details.list_price"
  },
  propertyType: {
    key: "details.property_type",
    values: {
      SingleFamily: "single_family_home",
      MultiFamily: "multi_tenant_home",
      Condo: "condominium",
      Manufactured: "single_family_home",
      Townhome: "townhome",
      NewConstruction: "single_family_home",
      Mobile: "single_family_home",
      default: "single_family_home"
    }
  },
  propertyUse: {
    key: "details.residency_type",
    values: {
      Primary: "primary_home",
      Secondary: "second_home",
      Investment: "rental_home",
      default: "primary_home"
    }
  },
  loanPurpose: {
    key: "details.loan_purpose",
    values: {
      Purchase: "purchase",
      Refinance: "refinance",
      default: "purchase"
    }
  },
  loanAmount: {
    key: "details.loan_amount"
  },
  hasAcceptedOffer: {
    key: "details.has_accepted_offer"
  },
  cashOutAmount: {
    key: "details.cashout_amount"
  },
  currentMortgageBalance: {
    key: "details.current_mortgage_balance"
  },
  currentMortgageRate: {
    key: "details.current_mortgage_rate"
  },
  jornayaLeadId: {
    key: "details.jornaya_lead_id"
  },
  trustedFormCertificateUrl: {
    key: "details.trusted_form_url"
  },
  source: {
    key: "details.lead_source"
  },
  sourceId: {
    key: "details.source_id"
  },
  anonymousId: {
    key: "details.anonymous_id"
  },
  sourceChannel: {
    key: "details.source_channel"
  },
  leadPricePaid: {
    key: "details.lead_price_paid"
  },
  dateLeadReceived: {
    key: "details.date_lead_received"
  },
  exclusive: {
    key: "details.exclusive"
  },
  coborrowerEmail: {
    key: "details.coborrower_email"
  },
  hasPreapproval: {
    key: "details.has_preapproval"
  },
  questionnaireStartTime: {
    key: "details.questionnaire_start_time"
  },
  questionnaireCompletionTime: {
    key: "details.questionnaire_completion_time"
  },
  purchaseTimeline: {
    key: "details.purchase_timeline_months"
  },
  closeDate: {
    key: "details.close_date"
  },
  accountId: {
    key: "details.account_id"
  },
  advisor: {
    key: "details.advisor_email"
  },
  hubspotContactId: {
    key: "details.hubspot_contact_id"
  },
  hubspotDealId: {
    key: "details.hubspot_deal_id"
  },
  loanType: {
    key: "details.loan_type",
    values: {
      Conventional: "conventional",
      "Homeready/Homepossible": "homeready",
      FHA: "fha",
      HELOC: "heloc",
      Reverse: "reverse",
      USDA: "usda",
      VA: "va",
      Jumbo: "jumbo",
      "Non QM": "non_qm",
      DPA: "dpa",
      Construction: "construction",
      Rehab: "rehab",
      Renovation: "renovation",
      default: "conventional"
    }
  },
  campaign: {
    key: "details.campaign"
  },
  campaignId: {
    key: "details.campaign_id"
  },
  comments: {
    key: "details.message"
  },
  currentProductType: {
    key: "details.current_product_type",
    values: {
      Fixed30: "fixed_30",
      Fixed20: "fixed_20",
      Fixed15: "fixed_15",
      Fixed10: "fixed_10",
      ARM10: "arm_10",
      ARM7: "arm_7",
      ARM5: "arm_5",
      Other: "other",
      default: "fixed_30"
    }
  },
  leadType: {
    key: "details.lead_type",
    values: {
      "web-lead": "web_lead",
      "direct-web-lead": "direct_web_lead",
      "preapproval-lead": "preapproval_lead",
      "preapproval-lead-tier-2": "preapproval_lead_tier_2",
      "rate-quote-lead": "rate_quote_lead",
      "click-out-lead": "click_out_lead",
      "rate-table-lead": "rate_table_lead",
      "recapture-from-concierge-lead": "recapture_lead",
      default: "web_lead"
    }
  },
  ownOrRent: {
    key: "details.own_or_rent",
    values: {
      Own: "own",
      Rent: "rent",
      default: "own"
    }
  },
  timeline: {
    key: "details.timeline",
    values: {
      "0-3Months": "0_3_months",
      "3-6Months": "3_6_months",
      "6+Months": "6_plus_months",
      default: "3_6_months"
    }
  },
  questionnaireType: {
    key: "details.questionnaire_type",
    values: {
      purchase: "purchase",
      preapproval: "preapproval",
      refinance: "refinance",
      "accepted-offer": "accepted_offer",
      default: "purchase"
    }
  },
  downPayment: {
    key: "details.down_payment"
  },
  employmentStatus: {
    key: "details.employment_status",
    values: {
      Employed: "employed",
      SelfEmployed: "self_employed",
      Retired: "retired",
      Unemployed: "unemployed",
      default: "employed"
    }
  },
  state: {
    key: "details.shopping_state"
  }
}

// MRC incoming lead mapping (for receiving leads from Mortgage Research Center)
export const MRC_INCOMING_LEAD_MAPPING: ConnectorFieldMapping = {
  Internal_LeadID: {
    key: "details.source_lead_id"
  },
  source: {
    key: "details.lead_source"
  },
  lead_price_paid: {
    key: "details.lead_cost"
  },
  first_name: { key: "first_name" },
  last_name: { key: "last_name" },
  phone: { key: "phone" },
  phone_home: {
    key: "details.phone_home"
  },
  phone_cell: {
    key: "phone"
  },
  phone_work: {
    key: "details.phone_work"
  },
  address: {
    key: "details.current_address"
  },
  address2: {
    key: "details.current_address2"
  },
  city: {
    key: "details.current_city"
  },
  state: {
    key: "details.current_state"
  },
  zip_code: {
    key: "details.current_zipcode"
  },
  county: {
    key: "details.current_county"
  },
  country: {
    key: "details.country"
  },
  ip_address: {
    key: "details.ip_address"
  },
  current_rate: {
    key: "details.current_mortgage_rate"
  },
  living_situation: {
    key: "details.living_situation",
    values: {
      own: "own",
      rent: "rent",
      other: "other",
      default: "own"
    }
  },
  military_status: {
    key: "details.military_status",
    values: {
      active: "active",
      veteran: "veteran",
      reserves: "reserves",
      spouse: "spouse",
      none: "none",
      default: "none"
    }
  },
  credit_rating: {
    key: "details.credit_score",
    values: {
      Excellent: 800,
      Good: 720,
      Fair: 660,
      Poor: 600,
      Bad: 550,
      default: 660
    }
  },
  email: {
    key: "email"
  },
  bankruptcy: {
    key: "details.foreclosure_bankruptcy",
    values: {
      N: "no",
      Y: "yes",
      "2year": "2_years",
      "5year": "5_years",
      "7year": "7_years",
      default: "no"
    }
  },
  leadid_token: {
    key: "details.jornaya_lead_id"
  },
  loan_product: {
    key: "details.loan_type",
    values: {
      va: "va",
      usda: "usda",
      fha: "fha",
      conventional: "conventional",
      default: "conventional"
    }
  },
  loan_purpose: {
    key: "details.loan_purpose",
    values: {
      P: "purchase",
      R: "refinance",
      default: "purchase"
    }
  },
  military: {
    key: "details.military_eligible",
    values: {
      N: "no",
      Y: "yes",
      default: "no"
    }
  },
  military_branch: {
    key: "details.military_branch",
    values: {
      Army: "army",
      Marine: "marine",
      Navy: "navy",
      Air: "air_force",
      Coast: "coast_guard",
      National: "national_guard",
      Spouse: "spouse",
      Other: "other",
      Civilian: "civilian",
      default: "civilian"
    }
  },
  property_state: {
    key: "details.location.state"
  },
  property_type: {
    key: "details.property_type",
    values: {
      single: "single_family_home",
      condominium: "condominium",
      townhome: "townhome",
      multifamily: "multi_tenant_home",
      other: "single_family_home",
      manufactured: "single_family_home",
      "new construction": "single_family_home",
      default: "single_family_home"
    }
  },
  property_value: {
    key: "details.list_price"
  },
  property_zip: {
    key: "details.location.zipcode"
  },
  real_estate_agent: {
    key: "details.has_agent",
    values: {
      N: "no",
      Y: "yes",
      default: "no"
    }
  },
  refi_balance: {
    key: "details.current_mortgage_balance"
  },
  refi_value: {
    key: "details.refi_property_value"
  },
  when_loan: {
    key: "details.purchase_readiness",
    values: {
      "signed purchase agreement": "considering_loan_offers_now",
      "offer pending": "considering_loan_offers_now",
      "within a month": "thirty_days_out",
      "2 to 3 months": "two_to_four_months",
      "3 to 6 months": "two_to_four_months",
      "over 6 months": "researching_options",
      "no time frame": "researching_options",
      now: "considering_loan_offers_now",
      soon: "thirty_days_out",
      someday: "researching_options",
      default: "two_to_four_months"
    }
  },
  refi_reason: {
    key: "details.refi_reason",
    values: {
      rate: "lower_rate",
      home_improvements: "home_improvements",
      payoff_debt: "payoff_debt",
      cashout: "cashout",
      consolidation: "consolidation",
      other: "other",
      lower_payments: "lower_payments",
      payoff_faster: "payoff_faster",
      default: "lower_rate"
    }
  },
  self_employed: {
    key: "details.self_employed",
    values: {
      N: "no",
      Y: "yes",
      default: "no"
    }
  },
  purchase_first: {
    key: "details.first_time_buyer",
    values: {
      N: "no",
      Y: "yes",
      default: "no"
    }
  },
  manufactured_own: {
    key: "details.manufactured_land_ownership",
    values: {
      own: "own",
      rent: "rent",
      default: "own"
    }
  },
  down_payment_pct: {
    key: "details.down_payment_percent"
  },
  property_url: {
    key: "details.property_url"
  },
  gross_income: {
    key: "details.household_income"
  },
  employment_status: {
    key: "details.employment_status",
    values: {
      employed: "employed",
      "self employed": "self_employed",
      retired: "retired",
      "not employed": "unemployed",
      default: "employed"
    }
  },
  property_city: {
    key: "details.location.city"
  },
  loan_term: {
    key: "details.loan_term"
  },
  addr_city: {
    key: "details.current_city"
  },
  addr_state: {
    key: "details.current_state"
  },
  down_payment: {
    key: "details.down_payment"
  },
  property_use: {
    key: "details.residency_type",
    values: {
      primary: "primary_home",
      secondary: "second_home",
      investment: "rental_home",
      default: "primary_home"
    }
  },
  property_county: {
    key: "details.location.county"
  },
  refi_current_loan_type: {
    key: "details.current_loan_type"
  },
  cash_outamt: {
    key: "details.cashout_amount"
  }
}

// Bankrate incoming lead mapping (for receiving leads from Bankrate)
export const BANKRATE_INCOMING_LEAD_MAPPING: ConnectorFieldMapping = {
  // Basic fields
  leadId: { key: "details.source_lead_id" },
  leadcost: { key: "details.lead_cost" },
  firstName: { key: "first_name" },
  lastName: { key: "last_name" },
  email: { key: "email" },
  phone: { key: "phone" },

  // Location
  zipCode: { key: "details.location.zipcode" },
  propertyCity: { key: "details.location.city" },
  propertyState: { key: "details.location.state" },

  // Loan info with transformations
  loanType: {
    key: "details.loan_purpose",
    values: {
      "Home Purchase": "purchase",
      "Home Refinance": "refinance",
      default: "purchase"
    }
  },
  loanAmount: { key: "details.loan_amount" },

  // Property with transformations
  propertyType: {
    key: "details.property_type",
    values: {
      "Single Family Home": "single_family_home",
      "Multi-Family Home": "multi_tenant_home",
      Condominium: "condominium",
      Townhome: "townhome",
      default: "single_family_home"
    }
  },
  propertyUse: {
    key: "details.residency_type",
    values: {
      "Primary Home": "primary_home",
      "Secondary Home": "second_home",
      "Rental Property": "rental_home",
      default: "primary_home"
    }
  },

  // Credit - direct pass-through
  creditProfileAmount: { key: "details.credit_score" },

  // Military/bankruptcy with transformations
  isMilitaryMember: {
    key: "details.military_eligible",
    values: {
      true: "yes",
      false: "no",
      default: "no"
    }
  },
  hasFiledBankruptcy: {
    key: "details.foreclosure_bankruptcy",
    values: {
      true: "yes",
      false: "no",
      default: "no"
    }
  },

  // Employment
  employmentStatus: {
    key: "details.employment_status",
    values: {
      Employed: "employed",
      "Not Employed": "unemployed",
      "Self Employed": "self_employed",
      Military: "military",
      Other: "other",
      default: "employed"
    }
  },
  hasProofIncome: { key: "details.has_proof_income" },

  // Product info
  productDesc: { key: "details.product_description" },
  productRate: { key: "details.product_rate" },
  productAPR: { key: "details.product_apr" },
  points: { key: "details.points" },
  leadSourceCampaign: { key: "details.campaign" },

  // Purchase-specific fields
  isFirstPropertyPurchase: {
    key: "details.first_time_buyer",
    values: {
      true: "yes",
      false: "no",
      default: "no"
    }
  },
  propertyPurchaseSituation: {
    key: "details.purchase_readiness",
    values: {
      "Offer Accepted by Seller": "considering_loan_offers_now",
      "Making Offers": "considering_loan_offers_now",
      "Going to Open Houses": "thirty_days_out",
      "Researching Options": "researching_options",
      default: "researching_options"
    }
  },
  purchasePriceAmount: { key: "details.list_price" },
  downPaymentAmount: { key: "details.down_payment" },
  downPaymentPercent: { key: "details.down_payment_percent" },
  rateTypeDesired: { key: "details.rate_type" },
  grossAnnualIncomeAmount: { key: "details.household_income" },
  isWorkingWithRealtor: {
    key: "details.has_agent",
    values: {
      true: "yes",
      false: "no",
      default: "no"
    }
  },

  // Refinance-specific fields
  housePurchaseYear: { key: "details.house_purchase_year" },
  propertyEstimatedValueAmount: { key: "details.list_price" },
  firstMortgageBalanceAmount: { key: "details.current_mortgage_balance" },
  firstMortgageRate: { key: "details.current_mortgage_rate" },
  existingRateType: { key: "details.existing_rate_type" },
  hasSecondMortgage: { key: "details.has_second_mortgage" },
  additionalCashAmount: { key: "details.cashout_amount" },
  averageMonthlyIncomeAmount: { key: "details.monthly_income" },
  averageMonthlyExpensesAmount: { key: "details.monthly_expenses" },
  hasLatePayments: { key: "details.has_late_payments" },
  hasFHALoan: { key: "details.has_fha_loan" }
}

// Bankrate incoming subscription mapping (for receiving subscriptions from Bankrate)
export const BANKRATE_INCOMING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = {
  // Basic fields
  firstName: { key: "first_name" },
  lastName: { key: "last_name" },
  email: { key: "email" },
  phone: { key: "phone" },

  // Location
  zipCode: { key: "quote_info.location.zipcode" },
  propertyCity: { key: "quote_info.location.city" },
  propertyState: { key: "quote_info.location.state" },

  // Loan info with transformations
  loanType: {
    key: "quote_info.loan_purpose",
    values: {
      "Home Purchase": "purchase",
      "Home Refinance": "refinance",
      default: "purchase"
    }
  },
  loanAmount: { key: "quote_info.loan_amount" },

  // Property with transformations
  propertyType: {
    key: "quote_info.property_type",
    values: {
      "Single Family Home": "single_family_home",
      "Multi-Family Home": "multi_tenant_home",
      Condominium: "condominium",
      Townhome: "townhome",
      default: "single_family_home"
    }
  },
  propertyUse: {
    key: "quote_info.residency_type",
    values: {
      "Primary Home": "primary_home",
      "Secondary Home": "second_home",
      "Rental Property": "rental_home",
      default: "primary_home"
    }
  },

  // Credit - direct pass-through
  creditProfileAmount: { key: "quote_info.credit_score" },

  // Purchase-specific fields
  purchasePriceAmount: { key: "quote_info.list_price" }
}

// MRC incoming subscription mapping (for receiving subscriptions from Mortgage Research Center)
export const MRC_INCOMING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = {
  first_name: { key: "first_name" },
  last_name: { key: "last_name" },
  email: { key: "email" },
  phone: { key: "phone" },
  phone_cell: { key: "phone" },
  loid: { key: "loid" },
  credit_rating: {
    key: "quote_info.credit_score",
    values: {
      Excellent: 800,
      Good: 720,
      Fair: 660,
      Poor: 600,
      Bad: 550,
      default: 660
    }
  },
  loan_product: {
    key: "quote_info.loan_type",
    values: {
      va: "va",
      usda: "usda",
      fha: "fha",
      conventional: "conventional",
      default: "conventional"
    }
  },
  loan_purpose: {
    key: "quote_info.loan_purpose",
    values: {
      P: "purchase",
      R: "refinance",
      default: "purchase"
    }
  },
  loan_amount: { key: "quote_info.loan_amount" },
  loan_term: { key: "quote_info.loan_term" },
  property_value: { key: "quote_info.list_price" },
  property_type: {
    key: "quote_info.property_type",
    values: {
      single: "single_family_home",
      condominium: "condominium",
      townhome: "townhome",
      multifamily: "multi_tenant_home",
      other: "single_family_home",
      manufactured: "single_family_home",
      "new construction": "single_family_home",
      default: "single_family_home"
    }
  },
  property_use: {
    key: "quote_info.residency_type",
    values: {
      primary: "primary_home",
      secondary: "second_home",
      investment: "rental_home",
      default: "primary_home"
    }
  },
  property_state: { key: "quote_info.location.state" },
  property_city: { key: "quote_info.location.city" },
  property_zip: { key: "quote_info.location.zipcode" },
  property_county: { key: "quote_info.location.county" }
}

// Zillow incoming lead mapping (for receiving leads from Zillow)
export const ZILLOW_INCOMING_LEAD_MAPPING: ConnectorFieldMapping = {
  // Lead identification
  id: { key: "details.source_lead_id" },
  price: { key: "details.lead_cost" },
  source: { key: "details.lead_source" },
  type: {
    key: "details.lead_type",
    values: {
      simple: "web_lead",
      quote: "rate_quote_lead",
      longForm: "preapproval_lead",
      propertyPreapproval: "preapproval_lead",
      zhl: "web_lead",
      default: "web_lead"
    }
  },
  brand: { key: "details.brand" },
  channel: { key: "details.source_channel" },
  created: { key: "details.date_lead_received" },

  // Sender/borrower basic info
  "sender.firstName": { key: "first_name" },
  "sender.lastName": { key: "last_name" },
  "sender.emailAddress": { key: "email" },
  "sender.phoneNumber": { key: "phone" },
  "sender.middleName": { key: "details.middle_name" },
  "sender.suffix": { key: "details.suffix" },

  // Recipient/lender info
  "recipient.lenderId": { key: "loid" },
  "recipient.nmlsLicense": { key: "details.nmls_license" },

  // Credit score - Zillow provides both low and high
  "details.creditScoreLow": { key: "details.credit_score" },
  "details.creditScoreHigh": { key: "details.credit_score_high" },
  "details.creditScoreRange": {
    key: "details.credit_score",
    values: {
      R_760_: 780,
      R_740_759: 749,
      R_720_739: 729,
      R_700_719: 709,
      R_680_699: 689,
      R_660_679: 669,
      R_640_659: 649,
      R_620_639: 629,
      R_600_619: 609,
      R_560_599: 579,
      R_300_559: 500,
      default: 650
    }
  },

  // Financial info
  "details.annualIncome": { key: "details.household_income" },
  "details.monthlyDebts": { key: "details.monthly_debts" },
  "details.totalAssets": { key: "details.total_assets" },
  "details.debtToIncomePercent": { key: "details.dti_ratio" },

  // Property location
  "details.streetAddress": { key: "details.location.street_address" },
  "details.city": { key: "details.location.city" },
  "details.county": { key: "details.location.county" },
  "details.stateAbbreviation": { key: "details.location.state" },
  "details.zipCode": { key: "details.location.zipcode" },
  "details.propertyAddress": { key: "details.location.street_address" },

  // Property info
  "details.propertyValue": { key: "details.list_price" },
  "details.propertyType": {
    key: "details.property_type",
    values: {
      SingleFamilyHome: "single_family_home",
      TownHouse: "townhome",
      CondoFourOrFewerStories: "condominium",
      CondoFiveOrMoreStories: "condominium",
      Cooperative: "condominium",
      MobileOrManufactured: "single_family_home",
      Modular: "single_family_home",
      Leasehold: "single_family_home",
      default: "single_family_home"
    }
  },
  "details.propertyUse": {
    key: "details.residency_type",
    values: {
      Primary: "primary_home",
      SecondaryOrVacation: "second_home",
      InvestmentOrRental: "rental_home",
      default: "primary_home"
    }
  },
  "details.newConstruction": { key: "details.new_construction" },
  "details.yearPurchased": { key: "details.year_purchased" },

  // Loan info
  "details.loanPurpose": {
    key: "details.loan_purpose",
    values: {
      Purchase: "purchase",
      Refinance: "refinance",
      HomeEquity: "refinance",
      Construction: "purchase",
      HELOC: "refinance",
      default: "purchase"
    }
  },
  "details.loanAmount": { key: "details.loan_amount" },
  "details.downPayment": { key: "details.down_payment" },
  "details.loanToValuePercent": { key: "details.ltv_ratio" },
  "details.cashOut": { key: "details.cashout_amount" },
  "details.wantsCashOut": { key: "details.wants_cashout" },
  "details.currentBalance": { key: "details.current_mortgage_balance" },

  // Loan program preferences
  "details.desiredPrograms": { key: "details.desired_programs" },
  "details.acceptPrepaymentPenalty": {
    key: "details.accept_prepayment_penalty"
  },
  "details.closingTimelineDays": { key: "details.closing_timeline_days" },

  // Borrower status
  "details.firstTimeBuyer": { key: "details.first_time_buyer" },
  "details.homeOwner": { key: "details.is_homeowner" },
  "details.selfEmployed": { key: "details.self_employed" },
  "details.hasCoborrower": { key: "details.has_coborrower" },
  "details.sellingHome": { key: "details.selling_home" },

  // Eligibility
  "details.vaEligible": { key: "details.va_eligible" },
  "details.fhaStreamlineEligible": { key: "details.fha_streamline_eligible" },
  "details.harpEligible": { key: "details.harp_eligible" },
  "details.isDpaEligible": { key: "details.dpa_eligible" },

  // VA-specific
  "details.vaFirstTimeUser": { key: "details.va_first_time_user" },
  "details.vaHasDisability": { key: "details.va_has_disability" },
  "details.veteranType": {
    key: "details.veteran_type",
    values: {
      RegularMilitary: "regular_military",
      Reserves: "reserves",
      NationalGuard: "national_guard",
      SpouseOfRegularMilitary: "military_spouse",
      SpouseOfReserveMilitary: "military_spouse",
      Army: "army",
      Navy: "navy",
      AirForce: "air_force",
      MarineCorps: "marine_corps",
      CoastGuard: "coast_guard",
      DepartmentOfDefense: "dod",
      default: "regular_military"
    }
  },

  // Bankruptcy/history
  "details.hasBankruptcy": { key: "details.foreclosure_bankruptcy" },
  "details.hasForeclosure": { key: "details.has_foreclosure" },
  "details.hasSecondMortgage": { key: "details.has_second_mortgage" },

  // Agent info
  "details.hasAgent": { key: "details.has_agent" },
  "details.agentFirstName": { key: "details.agent_first_name" },
  "details.agentLastName": { key: "details.agent_last_name" },
  "details.agentBusinessName": { key: "details.agent_business_name" },
  "details.agentEmailAddress": { key: "details.agent_email" },
  "details.agentPhoneNumber": { key: "details.agent_phone" },
  "details.agentId": { key: "details.agent_id" },

  // Other
  "details.message": { key: "details.message" },
  "details.preferredLanguage": {
    key: "details.preferred_language",
    values: {
      English: "english",
      Spanish: "spanish",
      Mandarin: "mandarin",
      Cantonese: "cantonese",
      Vietnamese: "vietnamese",
      Korean: "korean",
      Japanese: "japanese",
      Filipino: "filipino",
      Arabic: "arabic",
      French: "french",
      German: "german",
      Portuguese: "portuguese",
      Russian: "russian",
      Hindi: "hindi",
      default: "english"
    }
  },
  "details.preferredContactTime": { key: "details.preferred_contact_time" },
  "details.originalContactTime": { key: "details.original_contact_time" },
  "details.journeyStage": {
    key: "details.purchase_readiness",
    values: {
      LookingAtListings: "researching_options",
      TouringHomes: "thirty_days_out",
      MakingOffer: "considering_loan_offers_now",
      UnderContract: "considering_loan_offers_now",
      EstimatedAffordability: "researching_options",
      PreapprovedOrPrequalified: "thirty_days_out",
      MakingOfferOrUnderContract: "considering_loan_offers_now",
      default: "researching_options"
    }
  },
  "details.currentBacker": {
    key: "details.current_backer",
    values: {
      FannieMae: "fannie_mae",
      FreddieMac: "freddie_mac",
      FHA: "fha",
      default: "fannie_mae"
    }
  },
  "details.quoteId": { key: "details.quote_id" },
  "details.requestId": { key: "details.request_id" }
}

// LendingTree incoming lead mapping (for receiving leads from LendingTree)
export const LENDINGTREE_INCOMING_LEAD_MAPPING: ConnectorFieldMapping = {
  // Lead identification
  "lead.leadExternalId": { key: "details.source_lead_id" },
  "lead.vertical": {
    key: "details.loan_purpose",
    values: {
      "MORTGAGE.PURCHASE": "purchase",
      "MORTGAGE.REFINANCE": "refinance",
      HOMEEQUITY: "refinance",
      default: "purchase"
    }
  },
  "lead.requestType": { key: "details.lead_type" },

  // Consumer contact info
  "lead.consumer.contact.firstName": { key: "first_name" },
  "lead.consumer.contact.lastName": { key: "last_name" },
  "lead.consumer.contact.emailAddress": { key: "email" },
  "lead.consumer.contact.primaryPhone": { key: "phone" },

  // Consumer address (initial values, may be overwritten by property-specific address below)
  "lead.consumer.contact.streetAddress": {
    key: "details.location.street_address"
  },
  "lead.consumer.contact.city": { key: "details.location.city" },
  "lead.consumer.contact.state": { key: "details.location.state" },
  "lead.consumer.contact.zip": { key: "details.location.zipcode" },

  // Consumer profile
  "lead.consumer.profile.isMilitary": {
    key: "details.military_eligible",
    values: {
      true: "yes",
      false: "no",
      default: "no"
    }
  },
  "lead.consumer.profile.employmentStatus": {
    key: "details.employment_status",
    values: {
      EMPLOYED: "employed",
      SELFEMPLOYED: "self_employed",
      RETIRED: "retired",
      OTHER: "other",
      default: "employed"
    }
  },

  // Credit - selfCreditRating first (enum fallback), then assignedCredit overwrites with numeric score
  "lead.consumer.profile.selfCreditRating": {
    key: "details.credit_score",
    values: {
      EXCELLENT: 800,
      GOOD: 700,
      FAIR: 650,
      POOR: 600,
      default: 650
    }
  },
  "lead.consumer.profile.assignedCredit": { key: "details.credit_score" },

  // LendingTree metadata
  "lead.lendingTreeInformation.partnerExternalId": {
    key: "details.partner_external_id"
  },
  "lead.lendingTreeInformation.partnerName": {
    key: "details.partner_name"
  },
  "lead.lendingTreeInformation.filterName": {
    key: "details.filter_name"
  },
  "lead.lendingTreeInformation.filterRoutingId": {
    key: "details.filter_routing_id"
  },

  // === Purchase fields ===
  "lead.mortgagePurchase.loanAmount": { key: "details.loan_amount" },
  "lead.mortgagePurchase.propertyPurchasePrice": {
    key: "details.list_price"
  },
  "lead.mortgagePurchase.downPayment": { key: "details.down_payment" },
  "lead.mortgagePurchase.workingWithAgent": {
    key: "details.has_agent",
    values: {
      true: "yes",
      false: "no",
      default: "no"
    }
  },
  "lead.mortgagePurchase.property.type": {
    key: "details.property_type",
    values: {
      SINGLEFAMDET: "single_family_home",
      SINGLEFAMATT: "single_family_home",
      MULTIFAMILY: "multi_tenant_home",
      CONDO: "condominium",
      TOWNHOME: "townhome",
      default: "single_family_home"
    }
  },
  "lead.mortgagePurchase.property.use": {
    key: "details.residency_type",
    values: {
      OWNEROCCUPIED: "primary_home",
      SECONDARYVAC: "second_home",
      INVESTMENTRENTAL: "rental_home",
      default: "primary_home"
    }
  },
  "lead.mortgagePurchase.property.city": { key: "details.location.city" },
  "lead.mortgagePurchase.property.state": {
    key: "details.location.state"
  },
  "lead.mortgagePurchase.property.zip": {
    key: "details.location.zipcode"
  },
  "lead.mortgagePurchase.property.county": {
    key: "details.location.county"
  },

  // === Refinance fields ===
  "lead.mortgageRefinance.loanAmount": { key: "details.loan_amount" },
  "lead.mortgageRefinance.propertyValue": { key: "details.list_price" },
  "lead.mortgageRefinance.cashOut": { key: "details.cashout_amount" },
  "lead.mortgageRefinance.firstMortgageBalance": {
    key: "details.current_mortgage_balance"
  },
  "lead.mortgageRefinance.property.type": {
    key: "details.property_type",
    values: {
      SINGLEFAMDET: "single_family_home",
      SINGLEFAMATT: "single_family_home",
      MULTIFAMILY: "multi_tenant_home",
      CONDO: "condominium",
      TOWNHOME: "townhome",
      default: "single_family_home"
    }
  },
  "lead.mortgageRefinance.property.use": {
    key: "details.residency_type",
    values: {
      OWNEROCCUPIED: "primary_home",
      SECONDARYVAC: "second_home",
      INVESTMENTRENTAL: "rental_home",
      default: "primary_home"
    }
  },
  "lead.mortgageRefinance.property.city": {
    key: "details.location.city"
  },
  "lead.mortgageRefinance.property.state": {
    key: "details.location.state"
  },
  "lead.mortgageRefinance.property.zip": {
    key: "details.location.zipcode"
  },
  "lead.mortgageRefinance.property.county": {
    key: "details.location.county"
  },

  // === Home equity fields ===
  "lead.homeEquity.loanAmount": { key: "details.loan_amount" },
  "lead.homeEquity.propertyValue": { key: "details.list_price" },
  "lead.homeEquity.firstMortgageBalance": {
    key: "details.current_mortgage_balance"
  },
  "lead.homeEquity.secondMortgageBalance": {
    key: "details.second_mortgage_balance"
  },
  "lead.homeEquity.property.type": {
    key: "details.property_type",
    values: {
      SINGLEFAMDET: "single_family_home",
      SINGLEFAMATT: "single_family_home",
      MULTIFAMILY: "multi_tenant_home",
      CONDO: "condominium",
      TOWNHOME: "townhome",
      default: "single_family_home"
    }
  },
  "lead.homeEquity.property.use": {
    key: "details.residency_type",
    values: {
      OWNEROCCUPIED: "primary_home",
      SECONDARYVAC: "second_home",
      INVESTMENTRENTAL: "rental_home",
      default: "primary_home"
    }
  },
  "lead.homeEquity.property.city": { key: "details.location.city" },
  "lead.homeEquity.property.state": { key: "details.location.state" },
  "lead.homeEquity.property.zip": { key: "details.location.zipcode" },
  "lead.homeEquity.property.county": { key: "details.location.county" }
}

// LendingTree incoming subscription mapping (for receiving subscriptions from LendingTree)
export const LENDINGTREE_INCOMING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping =
  {
    // Consumer contact info
    "lead.consumer.contact.firstName": { key: "first_name" },
    "lead.consumer.contact.lastName": { key: "last_name" },
    "lead.consumer.contact.emailAddress": { key: "email" },
    "lead.consumer.contact.primaryPhone": { key: "phone" },

    // Consumer address (initial values, may be overwritten by property-specific address below)
    "lead.consumer.contact.city": { key: "quote_info.location.city" },
    "lead.consumer.contact.state": { key: "quote_info.location.state" },
    "lead.consumer.contact.zip": { key: "quote_info.location.zipcode" },

    // Loan purpose from vertical
    "lead.vertical": {
      key: "quote_info.loan_purpose",
      values: {
        "MORTGAGE.PURCHASE": "purchase",
        "MORTGAGE.REFINANCE": "refinance",
        HOMEEQUITY: "refinance",
        default: "purchase"
      }
    },

    // Credit - selfCreditRating first (enum fallback), then assignedCredit overwrites with numeric score
    "lead.consumer.profile.selfCreditRating": {
      key: "quote_info.credit_score",
      values: {
        EXCELLENT: 800,
        GOOD: 700,
        FAIR: 650,
        POOR: 600,
        default: 650
      }
    },
    "lead.consumer.profile.assignedCredit": {
      key: "quote_info.credit_score"
    },

    // === Purchase fields ===
    "lead.mortgagePurchase.loanAmount": { key: "quote_info.loan_amount" },
    "lead.mortgagePurchase.propertyPurchasePrice": {
      key: "quote_info.list_price"
    },
    "lead.mortgagePurchase.downPayment": { key: "quote_info.down_payment" },
    "lead.mortgagePurchase.property.type": {
      key: "quote_info.property_type",
      values: {
        SINGLEFAMDET: "single_family_home",
        SINGLEFAMATT: "single_family_home",
        MULTIFAMILY: "multi_tenant_home",
        CONDO: "condominium",
        TOWNHOME: "townhome",
        default: "single_family_home"
      }
    },
    "lead.mortgagePurchase.property.use": {
      key: "quote_info.residency_type",
      values: {
        OWNEROCCUPIED: "primary_home",
        SECONDARYVAC: "second_home",
        INVESTMENTRENTAL: "rental_home",
        default: "primary_home"
      }
    },
    "lead.mortgagePurchase.property.city": {
      key: "quote_info.location.city"
    },
    "lead.mortgagePurchase.property.state": {
      key: "quote_info.location.state"
    },
    "lead.mortgagePurchase.property.zip": {
      key: "quote_info.location.zipcode"
    },
    "lead.mortgagePurchase.property.county": {
      key: "quote_info.location.county"
    },

    // === Refinance fields ===
    "lead.mortgageRefinance.loanAmount": { key: "quote_info.loan_amount" },
    "lead.mortgageRefinance.propertyValue": { key: "quote_info.list_price" },
    "lead.mortgageRefinance.cashOut": { key: "quote_info.cashout_amount" },
    "lead.mortgageRefinance.firstMortgageBalance": {
      key: "quote_info.current_mortgage_balance"
    },
    "lead.mortgageRefinance.property.type": {
      key: "quote_info.property_type",
      values: {
        SINGLEFAMDET: "single_family_home",
        SINGLEFAMATT: "single_family_home",
        MULTIFAMILY: "multi_tenant_home",
        CONDO: "condominium",
        TOWNHOME: "townhome",
        default: "single_family_home"
      }
    },
    "lead.mortgageRefinance.property.use": {
      key: "quote_info.residency_type",
      values: {
        OWNEROCCUPIED: "primary_home",
        SECONDARYVAC: "second_home",
        INVESTMENTRENTAL: "rental_home",
        default: "primary_home"
      }
    },
    "lead.mortgageRefinance.property.city": {
      key: "quote_info.location.city"
    },
    "lead.mortgageRefinance.property.state": {
      key: "quote_info.location.state"
    },
    "lead.mortgageRefinance.property.zip": {
      key: "quote_info.location.zipcode"
    },
    "lead.mortgageRefinance.property.county": {
      key: "quote_info.location.county"
    },

    // === Home equity fields ===
    "lead.homeEquity.loanAmount": { key: "quote_info.loan_amount" },
    "lead.homeEquity.propertyValue": { key: "quote_info.list_price" },
    "lead.homeEquity.firstMortgageBalance": {
      key: "quote_info.current_mortgage_balance"
    },
    "lead.homeEquity.secondMortgageBalance": {
      key: "quote_info.second_mortgage_balance"
    },
    "lead.homeEquity.property.type": {
      key: "quote_info.property_type",
      values: {
        SINGLEFAMDET: "single_family_home",
        SINGLEFAMATT: "single_family_home",
        MULTIFAMILY: "multi_tenant_home",
        CONDO: "condominium",
        TOWNHOME: "townhome",
        default: "single_family_home"
      }
    },
    "lead.homeEquity.property.use": {
      key: "quote_info.residency_type",
      values: {
        OWNEROCCUPIED: "primary_home",
        SECONDARYVAC: "second_home",
        INVESTMENTRENTAL: "rental_home",
        default: "primary_home"
      }
    },
    "lead.homeEquity.property.city": { key: "quote_info.location.city" },
    "lead.homeEquity.property.state": { key: "quote_info.location.state" },
    "lead.homeEquity.property.zip": { key: "quote_info.location.zipcode" },
    "lead.homeEquity.property.county": { key: "quote_info.location.county" }
  }

// Zillow incoming subscription mapping (for receiving subscriptions from Zillow)
export const ZILLOW_INCOMING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = {
  // Sender/borrower basic info
  "sender.firstName": { key: "first_name" },
  "sender.lastName": { key: "last_name" },
  "sender.emailAddress": { key: "email" },
  "sender.phoneNumber": { key: "phone" },

  // Recipient/lender info
  "recipient.lenderId": { key: "loid" },

  // Credit score
  "details.creditScoreLow": { key: "quote_info.credit_score" },
  "details.creditScoreHigh": { key: "quote_info.credit_score_high" },
  "details.creditScoreRange": {
    key: "quote_info.credit_score",
    values: {
      R_760_: 780,
      R_740_759: 749,
      R_720_739: 729,
      R_700_719: 709,
      R_680_699: 689,
      R_660_679: 669,
      R_640_659: 649,
      R_620_639: 629,
      R_600_619: 609,
      R_560_599: 579,
      R_300_559: 500,
      default: 650
    }
  },

  // Property location
  "details.streetAddress": { key: "quote_info.location.street_address" },
  "details.city": { key: "quote_info.location.city" },
  "details.county": { key: "quote_info.location.county" },
  "details.stateAbbreviation": { key: "quote_info.location.state" },
  "details.zipCode": { key: "quote_info.location.zipcode" },
  "details.propertyAddress": { key: "quote_info.location.street_address" },

  // Property info
  "details.propertyValue": { key: "quote_info.list_price" },
  "details.propertyType": {
    key: "quote_info.property_type",
    values: {
      SingleFamilyHome: "single_family_home",
      TownHouse: "townhome",
      CondoFourOrFewerStories: "condominium",
      CondoFiveOrMoreStories: "condominium",
      Cooperative: "condominium",
      MobileOrManufactured: "single_family_home",
      Modular: "single_family_home",
      Leasehold: "single_family_home",
      default: "single_family_home"
    }
  },
  "details.propertyUse": {
    key: "quote_info.residency_type",
    values: {
      Primary: "primary_home",
      SecondaryOrVacation: "second_home",
      InvestmentOrRental: "rental_home",
      default: "primary_home"
    }
  },

  // Loan info
  "details.loanPurpose": {
    key: "quote_info.loan_purpose",
    values: {
      Purchase: "purchase",
      Refinance: "refinance",
      HomeEquity: "refinance",
      Construction: "purchase",
      HELOC: "refinance",
      default: "purchase"
    }
  },
  "details.loanAmount": { key: "quote_info.loan_amount" },
  "details.downPayment": { key: "quote_info.down_payment" },
  "details.loanToValuePercent": { key: "quote_info.ltv_ratio" },
  "details.cashOut": { key: "quote_info.cashout_amount" },
  "details.currentBalance": { key: "quote_info.current_mortgage_balance" },

  // Borrower status
  "details.firstTimeBuyer": { key: "quote_info.first_time_buyer" },
  "details.vaEligible": { key: "quote_info.va_eligible" },
  "details.fhaStreamlineEligible": {
    key: "quote_info.fha_streamline_eligible"
  },
  "details.harpEligible": { key: "quote_info.harp_eligible" }
}

// Own Up incoming subscription mapping (for receiving subscriptions from Own Up)
export const OWNUP_INCOMING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = {
  firstName: { key: "first_name" },
  lastName: { key: "last_name" },
  email: { key: "email" },
  phoneNumber: { key: "phone" },
  loid: { key: "loid" },
  creditRating: {
    key: "quote_info.credit_score",
    values: {
      Excellent: 800,
      Good: 700,
      Fair: 650,
      Poor: 600,
      "Very Poor": 550,
      default: 650
    }
  },
  vaEligible: { key: "quote_info.va_eligible" },
  fthb: { key: "quote_info.first_time_buyer" },
  propertyStreet: { key: "quote_info.location.street_address" },
  propertyCity: { key: "quote_info.location.city" },
  propertyState: { key: "quote_info.location.state" },
  propertyCounty: { key: "quote_info.location.county" },
  propertyZip: { key: "quote_info.location.zipcode" },
  propertyValue: { key: "quote_info.list_price" },
  propertyType: {
    key: "quote_info.property_type",
    values: {
      SingleFamily: "single_family_home",
      MultiFamily: "multi_tenant_home",
      Condo: "condominium",
      Manufactured: "single_family_home",
      Townhome: "townhome",
      NewConstruction: "single_family_home",
      Mobile: "single_family_home",
      default: "single_family_home"
    }
  },
  propertyUse: {
    key: "quote_info.residency_type",
    values: {
      Primary: "primary_home",
      Secondary: "second_home",
      Investment: "rental_home",
      default: "primary_home"
    }
  },
  loanPurpose: {
    key: "quote_info.loan_purpose",
    values: {
      Purchase: "purchase",
      Refinance: "refinance",
      default: "purchase"
    }
  },
  loanAmount: { key: "quote_info.loan_amount" },
  loanType: {
    key: "quote_info.loan_type",
    values: {
      Conventional: "conventional",
      "Homeready/Homepossible": "homeready",
      FHA: "fha",
      HELOC: "heloc",
      Reverse: "reverse",
      USDA: "usda",
      VA: "va",
      Jumbo: "jumbo",
      "Non QM": "non_qm",
      DPA: "dpa",
      Construction: "construction",
      Rehab: "rehab",
      Renovation: "renovation",
      default: "conventional"
    }
  },
  downPayment: { key: "quote_info.down_payment" },
  cashOutAmount: { key: "quote_info.cashout_amount" },
  currentMortgageBalance: { key: "quote_info.current_mortgage_balance" },
  currentMortgageRate: { key: "quote_info.current_mortgage_rate" }
}

// Generic incoming subscription mapping
export const GENERIC_INCOMING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = {
  first_name: { key: "first_name" },
  last_name: { key: "last_name" },
  email: { key: "email" },
  phone: { key: "phone" },
  loid: { key: "loid" },
  loan_purpose: {
    key: "quote_info.loan_purpose",
    values: {
      P: "purchase",
      R: "refinance",
      purchase: "purchase",
      refinance: "refinance",
      default: "purchase"
    }
  },
  loan_type: {
    key: "quote_info.loan_type",
    values: {
      va: "va",
      usda: "usda",
      fha: "fha",
      conventional: "conventional",
      default: "conventional"
    }
  },
  loan_amount: { key: "quote_info.loan_amount" },
  loan_term: { key: "quote_info.loan_term" },
  list_price: { key: "quote_info.list_price" },
  credit_score: { key: "quote_info.credit_score" },
  property_type: {
    key: "quote_info.property_type",
    values: {
      single: "single_family_home",
      single_family: "single_family_home",
      condominium: "condominium",
      condo: "condominium",
      townhome: "townhome",
      townhouse: "townhome",
      multifamily: "multi_tenant_home",
      multi_family: "multi_tenant_home",
      default: "single_family_home"
    }
  },
  residency_type: {
    key: "quote_info.residency_type",
    values: {
      primary: "primary_home",
      primary_home: "primary_home",
      secondary: "second_home",
      second_home: "second_home",
      investment: "rental_home",
      rental: "rental_home",
      default: "primary_home"
    }
  },
  state: { key: "quote_info.location.state" },
  city: { key: "quote_info.location.city" },
  zipcode: { key: "quote_info.location.zipcode" },
  zip: { key: "quote_info.location.zipcode" },
  county: { key: "quote_info.location.county" },
  street_address: { key: "quote_info.location.street_address" }
}

// Template definitions
export const CONNECTOR_TEMPLATES: ConnectorTemplate[] = [
  {
    name: "Zapier - Lead (Standard)",
    type: "zapier",
    data_type: "lead",
    field_mapping: ZAPIER_LEAD_MAPPING,
    description:
      "Standard field mapping for Zapier lead webhooks with camelCase field names"
  },
  {
    name: "Generic Webhook - Lead (Lowercase)",
    type: "webhook",
    data_type: "lead",
    field_mapping: GENERIC_LEAD_MAPPING,
    description:
      "Generic webhook mapping for leads with lowercase, no-underscore field names (common in many CRMs)"
  },
  {
    name: "Own Up - Incoming Lead",
    type: "webhook",
    data_type: "lead",
    field_mapping: OWNUP_INCOMING_LEAD_MAPPING,
    description:
      "Incoming lead mapping for Own Up mortgage marketplace with comprehensive borrower and property data"
  },
  {
    name: "MRC - Incoming Lead",
    type: "webhook",
    data_type: "lead",
    field_mapping: MRC_INCOMING_LEAD_MAPPING,
    description:
      "Incoming lead mapping for MRC (Mortgage Research Center) with comprehensive borrower, property, and refinance data"
  },
  {
    name: "Bankrate - Incoming Lead",
    type: "webhook",
    data_type: "lead",
    field_mapping: BANKRATE_INCOMING_LEAD_MAPPING,
    description:
      "Incoming lead mapping for Bankrate with purchase and refinance fields"
  },
  {
    name: "LendingTree - Incoming Lead",
    type: "webhook",
    data_type: "lead",
    field_mapping: LENDINGTREE_INCOMING_LEAD_MAPPING,
    description:
      "Incoming lead mapping for LendingTree with support for purchase, refinance, and home equity verticals"
  },
  {
    name: "Bankrate - Incoming Subscription",
    type: "webhook",
    data_type: "subscription",
    field_mapping: BANKRATE_INCOMING_SUBSCRIPTION_MAPPING,
    description:
      "Incoming subscription mapping for Bankrate with purchase and refinance fields"
  },
  {
    name: "Zillow - Incoming Lead",
    type: "webhook",
    data_type: "lead",
    field_mapping: ZILLOW_INCOMING_LEAD_MAPPING,
    description:
      "Incoming lead mapping for Zillow Lender Contact Notification API with comprehensive borrower, property, and loan data"
  },
  {
    name: "Zillow - Incoming Subscription",
    type: "webhook",
    data_type: "subscription",
    field_mapping: ZILLOW_INCOMING_SUBSCRIPTION_MAPPING,
    description:
      "Incoming subscription mapping for Zillow with property and loan quote data"
  },
  {
    name: "LendingTree - Incoming Subscription",
    type: "webhook",
    data_type: "subscription",
    field_mapping: LENDINGTREE_INCOMING_SUBSCRIPTION_MAPPING,
    description:
      "Incoming subscription mapping for LendingTree with support for purchase, refinance, and home equity verticals"
  },
  {
    name: "Zapier - Subscription",
    type: "zapier",
    data_type: "subscription",
    field_mapping: ZAPIER_SUBSCRIPTION_MAPPING,
    description: "Standard field mapping for Zapier subscription webhooks"
  },
  {
    name: "Generic - Incoming Subscription",
    type: "webhook",
    data_type: "subscription",
    field_mapping: GENERIC_INCOMING_SUBSCRIPTION_MAPPING,
    description:
      "Generic incoming subscription mapping with value transformations for common field formats"
  },
  {
    name: "Own Up - Incoming Subscription",
    type: "webhook",
    data_type: "subscription",
    field_mapping: OWNUP_INCOMING_SUBSCRIPTION_MAPPING,
    description:
      "Incoming subscription mapping for Own Up mortgage marketplace with property and loan data"
  },
  {
    name: "MRC - Incoming Subscription",
    type: "webhook",
    data_type: "subscription",
    field_mapping: MRC_INCOMING_SUBSCRIPTION_MAPPING,
    description:
      "Incoming subscription mapping for MRC (Mortgage Research Center) with property and loan data"
  },
  {
    name: "Zapier - Pending Subscription",
    type: "zapier",
    data_type: "pending_subscription",
    field_mapping: ZAPIER_PENDING_SUBSCRIPTION_MAPPING,
    description:
      "Standard field mapping for Zapier pending subscription webhooks"
  }
]

/**
 * Get available templates filtered by data type
 */
export const getTemplatesByDataType = (
  dataType: "lead" | "subscription" | "pending_subscription"
): ConnectorTemplate[] => {
  return CONNECTOR_TEMPLATES.filter(t => t.data_type === dataType)
}

/**
 * Get template by name
 */
export const getTemplateByName = (
  name: string
): ConnectorTemplate | undefined => {
  return CONNECTOR_TEMPLATES.find(t => t.name === name)
}

/**
 * Get default template for a given CRM type and data type
 */
export const getDefaultTemplate = (
  crmType: string,
  dataType: "lead" | "subscription" | "pending_subscription"
): ConnectorTemplate | undefined => {
  return CONNECTOR_TEMPLATES.find(
    t => t.type === crmType && t.data_type === dataType
  )
}

// Field reference documentation for help text
export const LEAD_FIELD_OPTIONS = [
  { value: "email", label: "Email", category: "Basic" },
  { value: "phone", label: "Phone", category: "Basic" },
  { value: "first_name", label: "First Name", category: "Basic" },
  { value: "last_name", label: "Last Name", category: "Basic" },
  {
    value: "details.loan_purpose",
    label: "Loan Purpose",
    category: "Loan Info"
  },
  { value: "details.loan_type", label: "Loan Type", category: "Loan Info" },
  { value: "details.loan_amount", label: "Loan Amount", category: "Loan Info" },
  { value: "details.loan_term", label: "Loan Term", category: "Loan Info" },
  {
    value: "details.credit_score",
    label: "Credit Score",
    category: "Loan Info"
  },
  {
    value: "details.down_payment",
    label: "Down Payment",
    category: "Loan Info"
  },
  { value: "details.list_price", label: "List Price", category: "Loan Info" },
  {
    value: "details.property_type",
    label: "Property Type",
    category: "Property"
  },
  {
    value: "details.residency_type",
    label: "Residency Type",
    category: "Property"
  },
  { value: "details.realtor", label: "Realtor", category: "Property" },
  { value: "details.location.city", label: "City", category: "Location" },
  { value: "details.location.state", label: "State", category: "Location" },
  {
    value: "details.location.zipcode",
    label: "Zip Code",
    category: "Location"
  },
  {
    value: "details.location.street_address",
    label: "Street Address",
    category: "Location"
  },
  { value: "details.location.county", label: "County", category: "Location" },
  { value: "details.message", label: "Message", category: "Other" }
]

export const SUBSCRIPTION_FIELD_OPTIONS = [
  { value: "email", label: "Email", category: "Basic" },
  { value: "phone", label: "Phone", category: "Basic" },
  { value: "first_name", label: "First Name", category: "Basic" },
  { value: "last_name", label: "Last Name", category: "Basic" },
  { value: "loid", label: "Loan Officer ID", category: "Basic" },
  { value: "type", label: "Type", category: "Basic" },
  { value: "active", label: "Active", category: "Basic" },
  {
    value: "quote_info.loan_purpose",
    label: "Loan Purpose",
    category: "Quote Info"
  },
  {
    value: "quote_info.credit_score",
    label: "Credit Score",
    category: "Quote Info"
  },
  {
    value: "quote_info.property_type",
    label: "Property Type",
    category: "Quote Info"
  },
  {
    value: "quote_info.residency_type",
    label: "Residency Type",
    category: "Quote Info"
  },
  {
    value: "quote_info.list_price",
    label: "List Price",
    category: "Quote Info"
  },
  {
    value: "quote_info.loan_amount",
    label: "Loan Amount",
    category: "Quote Info"
  },
  {
    value: "quote_info.loan_term",
    label: "Loan Term",
    category: "Quote Info"
  },
  {
    value: "quote_info.loan_type",
    label: "Loan Type",
    category: "Quote Info"
  },
  {
    value: "quote_info.location.state",
    label: "State",
    category: "Location"
  },
  { value: "quote_info.location.city", label: "City", category: "Location" },
  {
    value: "quote_info.location.zipcode",
    label: "Zip Code",
    category: "Location"
  },
  {
    value: "quote_info.location.street_address",
    label: "Street Address",
    category: "Location"
  },
  {
    value: "quote_info.location.county",
    label: "County",
    category: "Location"
  }
]

export const PENDING_SUBSCRIPTION_FIELD_OPTIONS = [
  ...SUBSCRIPTION_FIELD_OPTIONS,
  { value: "trigger_rate", label: "Trigger Rate", category: "Basic" }
]
